import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store, createSelector } from '@ngrx/store';
import { MutationOptionsAlone } from 'apollo-angular/types';
import { cloneDeep } from 'lodash';
import MiniSearch, { SearchOptions } from 'minisearch';

import { ConfirmationService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { map, of, tap, filter, exhaustMap, catchError, switchMap } from 'rxjs';

import { CreateCalendarEventGQL, CreateCalendarEventMutationVariables, CreateYemboMoveGQL, CreateYemboSelfSurveyGQL, CreateYemboSelfSurveyMutation, CreateYemboSelfSurveyMutationVariables  } from '../../../generated/graphql.generated';
import { generateMutationContext } from '../../core/auth/session';
import { FreyaNotificationsService } from '../../services/freya-notifications.service';
import { createConfigSelector } from '../../state/branding.store';

import { generateUUID } from '../../utilities/state.util';
import { JobCreateInventoryActions } from '../job-create/job-create-inventory/job-create-inventory.actions';
import { selectIsJobCreationToolOpen } from '../job-create/job-create-state/job-create.selectors';
import { JobToolActions } from '../job-tool.actions';
import { selectJob, selectCustomer, selectJobEvents, selectJobId, selectQubeSheetsProjectId } from '../job-tool.reducer';




import { CreateSelfSurveyLinkDialogComponent } from './create-self-survey-link-dialog/create-self-survey-link-dialog.component';
import { InventoryActions } from './job-inventory.actions';
import { jobInventoryFeature } from './job-inventory.feature';
import { InventoryItem, InventoryItemDefinition, InventoryRoom, jobInventoryInitialState } from './job-inventory.state';

export const searchForInventoryItem = createEffect((
	actions$ = inject(Actions),
	store = inject(Store),
) => {

	let loaded = false;

	const itemMiniSearch = new MiniSearch({
		fields: ['reviewerName', 'label', 'rooms', 'aliases'],
		storeFields: [
			'name',
			'reviewerName',
			'label',
			'packing',
			'quantity',
			'volume',
			'weight',
			'generated',
			'rooms'
		],
		idField: 'label',
	});

	const itemMap: {
		[id: string]: InventoryItemDefinition,
	} = {};

	const searchOptions: SearchOptions = {
			prefix: true,
			fuzzy: 0.2,
			boost: {
				reviewerName: 2,
				label: 2,
			}
	}

	return actions$.pipe(
		ofType(
		  InventoryActions.itemAutocompleteSearchFired,
			JobCreateInventoryActions.itemAutocompleteSearchFired,
		),
		concatLatestFrom(() => store.select(createConfigSelector('INVENTORY_ITEMS'))),
		tap(([ _action, configItems ]) => {
			if (!loaded) {
				const items = configItems || [];
				loaded = true;
				itemMiniSearch.addAll(cloneDeep(items));
				for (const item of items) {
					itemMap[item.label] = item as unknown as InventoryItemDefinition;
				}
			}
		}),
		map(([ { query } ]) => itemMiniSearch.search(query, searchOptions)),
		map((results) => results.map((r) => itemMap[r.id]).filter(Boolean)),
		map((items) => InventoryActions.itemSearchSuccess({ items })),
	);

}, { functional: true, dispatch: true });

export const addRoom = createEffect((
	actions$ = inject(Actions),
	store = inject(Store),
) => {
		return actions$.pipe(
			ofType(
				InventoryActions.newRoomSelected,
				InventoryActions.addCustomRoomButtonClicked,
				InventoryActions.customRoomNameEnterPressed,
				JobCreateInventoryActions.addRoomButtonClicked,
			),
			concatLatestFrom((action) => {

				if (action.type === InventoryActions.newRoomSelected.type) {
					return of(action.name);
				}

				if (action.type === JobCreateInventoryActions.addRoomButtonClicked.type) {
					return of('');
				}

				return store.select(createSelector(
				  jobInventoryFeature.selectInventoryEditor,
					(inventoryEditor) => inventoryEditor.customRoomName.trim(),
				));
			}),
			map(([ _action, name ]): InventoryRoom => ({
          id: generateUUID(),
          name,
          inventory: [],
          totalItems: 0,
          totalWeight: 0,
          totalVolume: 0,
          totalBoxes: 0,
          isCollapsed: false,
			})),
			map((room) => InventoryActions.roomAdded({ room })),
		);

}, { functional: true, dispatch: true });

export const addItem = createEffect((
	actions$ = inject(Actions),
	store = inject(Store),
) => {
		return actions$.pipe(
			ofType(
				InventoryActions.itemAutocompleteItemSelected,
				InventoryActions.itemAutocompleteSaveButtonClicked,
				InventoryActions.itemAutocompleteEnterPressed,
				JobCreateInventoryActions.itemAutocompleteItemSelected,
			),
			filter((action) => {

				if (action.type !== InventoryActions.itemAutocompleteEnterPressed.type) {
					return true;
				}

				return action.key.key === 'Enter';
			}),
			concatLatestFrom((action) => store.select(createSelector(
			  jobInventoryFeature.selectInventoryEditor,
				(inventoryEditor): InventoryItemDefinition => {

					if ('item' in action) {
						return {
							...action.item,
							name: action.item.reviewerName || action.item.label,
							volume: parseFloat(action.item.volume as any),
							weight: parseFloat(action.item.weight as any),
						};
					}

					if (inventoryEditor.inventorySearchResults.length > 0) {

						const [ firstSearchResult ] = inventoryEditor.inventorySearchResults;

						return {
							...firstSearchResult,
							name: firstSearchResult.reviewerName || firstSearchResult.label,
							volume: parseFloat(firstSearchResult.volume as any),
							weight: parseFloat(firstSearchResult.weight as any),
						};
					}

					const customItemName = inventoryEditor.inventorySearch[action.roomId].trim();

					return {
						name: customItemName,
						reviewerName: customItemName,
						label: customItemName,
						volume: 0,
						weight: 0,
						generated: false,
					}
				},
			))),
			filter(([ _action, { name }]) => Boolean(name)),
			map(([ { roomId }, itemDefinition ]): {
				item: InventoryItem;
				roomId: string;
			} => {
				return {
					roomId,
					item: {
						...itemDefinition,
						id: generateUUID(),
						quantity: 1,
						volume: typeof itemDefinition.volume === 'string' ? parseFloat(itemDefinition.volume) : itemDefinition.volume,
						weight: typeof itemDefinition.weight === 'string' ? parseFloat(itemDefinition.weight) : itemDefinition.weight,
					},
				}
			}),
			map(({ item, roomId }) => InventoryActions.itemAdded({ item, roomId })),
		);
	}, { functional: true, dispatch: true });

export const editItem = createEffect((
	actions$ = inject(Actions),
	store = inject(Store),
) => {
		return actions$.pipe(
			ofType(InventoryActions.editItemDialogAcceptButtonClicked),
			concatLatestFrom(() => store.select(createSelector(
				jobInventoryFeature.selectInventoryEditor,
				jobInventoryFeature.selectEditInventoryItemDialog,
				(inventoryEditor, editInventoryItemDialog): {
					item: InventoryItem;
					roomId: string;
				} => {

					const {
						selectedItem,
						name,
						quantity,
						weight,
						volume,
					} = editInventoryItemDialog;

					const rooms = cloneDeep(inventoryEditor.rooms);

					const room = rooms.find((r) => r.id === selectedItem.roomId);

					const originalItem = room.inventory.find((i) => i.id === selectedItem.itemId);

					return {
						item: {
							...originalItem,
							name,
							quantity,
							weight,
							volume,
						},
						roomId: selectedItem.roomId,
					}
				}
			))),
			map(([ _action, payload ]) => InventoryActions.itemEdited(payload)),
		);
}, { functional: true, dispatch: true });

export const viewMoveInYembo = createEffect((
	actions$ = inject(Actions),
	store = inject(Store),
) => {
	return actions$.pipe(
		ofType(
			InventoryActions.viewMoveInYemboButtonClicked,
		),
		concatLatestFrom(() => store.select(selectJob)),
		tap(([_, job]) => {

			const yemboEmployeeLink = job?.metadata?.yemboEmployeeLink;

			if (yemboEmployeeLink) {
				window.open(yemboEmployeeLink, '_blank');
			} else {
				console.error('Yembo link not found');
			}
		}),
	);
}, { functional: true, dispatch: false });

export const viewQubeSheetsProject = createEffect((
	actions$ = inject(Actions),
	store = inject(Store),
) => {
	return actions$.pipe(
		ofType(
			InventoryActions.viewQubeSheetsProjectButtonClicked,
		),
		exhaustMap((action) => of(action).pipe(
			concatLatestFrom(() => store.select(selectQubeSheetsProjectId)),
			map(([ _action, qubeSheetsProjectId ]) => {

				if (!qubeSheetsProjectId) {
					throw new Error('Qube Sheets project not found');
				}

				window.open(`https://app.qubesheets.com/projects/${qubeSheetsProjectId}`, '_blank');

				return InventoryActions.viewQubeSheetsProjectButtonSucceeded();
			}),
			catchError((error) => of(InventoryActions.viewQubeSheetsProjectButtonFailed({ error }))),
		)),
	);
}, { functional: true, dispatch: true });

export const openStartOnsiteDialog = createEffect((
	actions$ = inject(Actions),
	store = inject(Store),
	confirmationService = inject(ConfirmationService),
	startOnsiteGQL = inject(CreateYemboMoveGQL),
	notify = inject(FreyaNotificationsService),
  ) => {
	return actions$.pipe(
	  ofType(InventoryActions.startOnsiteButtonClicked),
		concatLatestFrom(() => store.select(selectJobId)),
	  tap(([ _, jobId ]) => {

			confirmationService.confirm({
				header: `Start On-Site Estimate`,
				message: `You will be redirected to Yembo.`,
				accept: () => {

					store.dispatch(InventoryActions.startOnsiteConfirmButtonClicked());

					const errorMessage = 'Failed to start onsite estimate';

					startOnsiteGQL.mutate({ jobId }).subscribe({
						next: (res) => {

							const { customerLink = null, employeeLink = null } = res.data?.createYemboMove || {};

							if (!customerLink || !employeeLink) {

								notify.error(errorMessage);

								store.dispatch(InventoryActions.startOnsiteError({
									error: new Error(errorMessage),
								}));

								return;
							}

							window.open(employeeLink, '_blank')?.focus();

							store.dispatch(InventoryActions.startOnsiteSuccess({
								employeeLink,
								customerLink,
								jobId,
							}));
						},
						error: (error) => {

							notify.error(errorMessage, error);

							store.dispatch(InventoryActions.startOnsiteError({
								error: new Error(errorMessage),
							}));
						}
					});
				},
			});

	  })
	);
  }, { functional: true, dispatch: false });

export const openSelfSurveyDialog = createEffect((
	actions$ = inject(Actions),
	store = inject(Store),
	dialogService = inject(DialogService),
) => {
	return actions$.pipe(
		ofType(InventoryActions.createSelfSurveyLinkMenuItemClicked),
		concatLatestFrom(() => store.select(selectCustomer)),
    map(([ _, customer ]) => dialogService.open(CreateSelfSurveyLinkDialogComponent, {
      header: 'Create Self Survey Link',
      data: { customer },
    })),
		exhaustMap((dialog) => dialog.onClose.pipe(
      map((confirmed) => InventoryActions.createSelfSurveyLinkConfirmationDialogClosed({ confirmed })),
    )),
	);
}, { functional: true, dispatch: true });

export const createSelfSurveyLink = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
  createYemboSelfSurveyGQL = inject(CreateYemboSelfSurveyGQL),
) => {
    return actions$.pipe(
      ofType(InventoryActions.createSelfSurveyLinkConfirmationDialogClosed),
      filter(({ confirmed }) => confirmed),
      concatLatestFrom(() => store.select(selectJobId)),
      map(([ _, jobId ]): {
        vars: CreateYemboSelfSurveyMutationVariables;
        opts: MutationOptionsAlone<CreateYemboSelfSurveyMutation, CreateYemboSelfSurveyMutationVariables>;
        jobId: string;
      } => ({
        vars: {
          jobId,
          sendEmail: true,
          sendSms: true,
        },
        opts: {
          context: generateMutationContext(),
        },
        jobId,
      })),
      switchMap(({ vars, opts, jobId }) => createYemboSelfSurveyGQL.mutate(vars, opts).pipe(
        map((res) => {

          const { link: customerLink = null, employeeLink = null } = res.data?.createYemboSelfSurvey || {};

          if (!customerLink || !employeeLink) {
            throw new Error('Failed to create self survey');
          }

          return InventoryActions.createSelfSurveyLinkSuccess({
            customerLink,
            employeeLink,
            jobId,
          });
        }),
        catchError((error) => of(InventoryActions.createSelfSurveyLinkError({ error }))),
      )),
    );
  }, { functional: true, dispatch: true });

export const setInitialYemboOptions = createEffect((
	actions$ = inject(Actions),
	store = inject(Store),
) => {
		return actions$.pipe(
			ofType(JobToolActions.jobLoaded),
			concatLatestFrom(() => [
				store.select(selectJob),
				store.select(selectIsJobCreationToolOpen),
			]),
			filter(([ _action, _job, isJobCreationToolOpen ]) => isJobCreationToolOpen),
			map(([ _action, job ]) => {

				const yemboOptions: typeof jobInventoryInitialState['yemboOptions'] = [];

				const hasSelfSurveyTag = job?.tags?.some(t => t.category === 'Self Survey');

				if (hasSelfSurveyTag) {
					yemboOptions.push('selfSurvey');
				}

				const hasCreatorId = Boolean(job?.metadata?.yemboMoveCreatorId);
				const hasVose = job?.events?.some((e) => e.type === 'virtualEstimate');

				if (hasCreatorId || hasVose) {
					yemboOptions.push('smartConsult');
				}

				return yemboOptions;
			}),
			map((yemboOptions) => InventoryActions.initialYemboOptionsLoaded({ yemboOptions })),
		);
}, { functional: true, dispatch: true });

export const createVirtualEstimate = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
  createCalendarEventGQL = inject(CreateCalendarEventGQL),
) => {

	return actions$.pipe(
		ofType(
			InventoryActions.bookYemboSmartConsultButtonClicked,
			InventoryActions.bookVideoCallButtonClicked,
		),
		concatLatestFrom(() => [
			store.select(selectJobId),
			store.select(selectJobEvents),
		]),
    exhaustMap((action) => of(action).pipe(
      map(([ _, jobId, jobEvents]): {
        vars: CreateCalendarEventMutationVariables;
        jobId: string;
      } => ({
        vars: {
          calendarEvents: [
            {
              type: 'virtualEstimate',
              title: 'Virtual Estimate',
              status: 'required',
              jobId: jobId,
              sequentialOrder: (jobEvents?.length || 0) + 1,
            },
          ],
        },
        jobId,
      })),
      switchMap(({ vars, jobId }) => createCalendarEventGQL.mutate(vars).pipe(
        map((res) => res.data.createCalendarEvent.events[0]),
        map((createdEvent) => {
          return InventoryActions.createVirtualEstimateSuccess({
            createdEvent,
            jobId,
            eventId: createdEvent.id,
          });
        }),
      )),
      catchError((error) => of(InventoryActions.createVirtualEstimateError({ error }))),
    ),
    ),
	);

}, { functional: true, dispatch: true });
